/**
 * useConfirm — Programmatic Confirmation Dialog
 * Returns a Promise<boolean>: resolves true (confirmed) or false (cancelled).
 *
 * Usage:
 *   import { useConfirm } from '@/composables/useConfirm'
 *   const confirm = useConfirm()
 *   const ok = await confirm({
 *     title: 'Delete Invoice',
 *     message: 'This action cannot be undone.',
 *     confirmLabel: 'Delete',
 *     variant: 'destructive',
 *   })
 *   if (ok) { ... proceed ... }
 */
import { reactive } from "vue";

const state = reactive({
  isOpen: false,
  title: "",
  message: "",
  confirmLabel: "Confirm",
  cancelLabel: "Cancel",
  variant: "default", // 'default' | 'destructive' | 'warning'
  // Internal resolve function for the pending Promise
  _resolve: null,
});

/**
 * Open a confirmation dialog.
 * @param {{ title: string, message: string, confirmLabel?: string, cancelLabel?: string, variant?: string }} options
 * @returns {Promise<boolean>}
 */
function openDialog(options) {
  return new Promise((resolve) => {
    state.isOpen = true;
    state.title = options.title || "Confirm Action";
    state.message = options.message || "Are you sure you want to proceed?";
    state.confirmLabel = options.confirmLabel || "Confirm";
    state.cancelLabel = options.cancelLabel || "Cancel";
    state.variant = options.variant || "default";
    state._resolve = resolve;
  });
}

/** Called by AppConfirmDialog on confirm button click */
function resolve(value) {
  if (state._resolve) {
    state._resolve(value);
    state._resolve = null;
  }
  state.isOpen = false;
}

export const confirmDialogState = state;
export const confirmDialogResolve = resolve;

/**
 * Composable entry point.
 * Returns a function: `confirm(options) => Promise<boolean>`
 */
export function useConfirm() {
  return openDialog;
}

export default useConfirm;
